/**
 * @file Destructible component: marks a prop that can be broken.
 *
 * Destructible props are individual entities with their own collider and health
 * — never merged into the arena's static blocker rectangles — so a single one
 * can die without disturbing its neighbours. The numbers here come from a
 * {@link ../config/DungeonProps.js DungeonProps} row; a prop row with no
 * `destructible` block never gains this component and stays indestructible.
 *
 * The runtime links back to its presentation and its collider are filled in by
 * {@link ../world/dungeon/DungeonArena.js} once the entity is placed: `handle`
 * points at the InstancedMesh slot to hide on death, and `blocker` is the AABB
 * to splice out of the arena's spawn-avoidance list.
 */

import { Component } from '../ecs/Component.js';

export class Destructible extends Component {
  /**
   * @param {object} options Setup, usually a {@link ../config/DungeonProps.js} `destructible` row.
   * @param {string} options.type Prop kind, e.g. `'crate'` or `'barrel'`.
   * @param {string[]} [options.destroyedBy] Damage types that may break it.
   * @param {number} [options.debrisCount] Debris particles spawned on death.
   * @param {boolean} [options.leavesRubble] Whether a flat rubble decal remains.
   * @param {string | null} [options.loot] Powerup key to drop on death, or null.
   */
  constructor({
    type,
    destroyedBy = ['explosion', 'bullet', 'melee'],
    debrisCount = 6,
    leavesRubble = false,
    loot = null
  }) {
    super();
    /** @type {string} */
    this.type = type;
    /** @type {string[]} Damage types this prop reacts to. */
    this.destroyedBy = destroyedBy;
    /** @type {number} */
    this.debrisCount = debrisCount;
    /** @type {boolean} */
    this.leavesRubble = leavesRubble;
    /** @type {string | null} */
    this.loot = loot;

    /**
     * Presentation handle assigned by the builder: which InstancedMesh slot to
     * hide, and which rubble slot to reveal. Null until mounted.
     * @type {import('../world/dungeon/DungeonBuilder.js').DestructibleHandle | null}
     */
    this.handle = null;
    /**
     * The arena's spawn-avoidance AABB for this prop, spliced out on death so
     * the freed floor can host spawns again. Null until placed.
     * @type {{ x: number, z: number, halfX: number, halfZ: number } | null}
     */
    this.blocker = null;
  }

  /**
   * @param {string | undefined} type Damage type from the `damage` event.
   * @returns {boolean} Whether this prop reacts to that damage type. Untyped
   *   damage (no `type` on the event) always lands, so generic sources keep
   *   working.
   */
  acceptsDamageType(type) {
    return !type || this.destroyedBy.includes(type);
  }

  /** @returns {void} */
  reset() {
    this.handle = null;
    this.blocker = null;
  }
}
